import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Video } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Ama {
    id: number;
    title: string;
    description: string | null;
    google_meet_url: string | null;
}

interface Props {
    amas: Ama[];
}

const LiveClassesContent: React.FC<Props> = ({ amas }) => {
    const { t } = useTranslation();

    // Check if description contains HTML tags
    const isHtmlContent = (description: string | null): boolean => {
        if (!description) return false;
        return /<\/?[a-z][\s\S]*>/i.test(description);
    };

    // Parse description into bullet points (for plain text descriptions)
    const parseDescription = (description: string | null): string[] => {
        if (!description) return [];
        return description
            .split('\n')
            .filter((line) => line.trim() !== '')
            .map((line) => line.trim());
    };

    return (
        <AppLayout>
            <PageTitle title="Clases en Vivo" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Page Header */}
                <div className="space-y-4">
                    <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                        {t('liveClasses.title')}
                    </h1>
                </div>

                {/* Hardcoded 1-on-1 Session Card */}
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {t('liveClasses.oneOnOne.title')}
                    </h2>
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <ul className="mb-6 space-y-3 text-gray-700">
                            <li className="flex items-start">
                                <span className="mr-2">•</span>
                                <span>{t('liveClasses.oneOnOne.point1')}</span>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2">✨</span>
                                <span>
                                    <strong>
                                        {t('liveClasses.oneOnOne.point2')}
                                    </strong>
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2">💡</span>
                                <span>
                                    <strong>
                                        {t('liveClasses.oneOnOne.point3Bold')}
                                    </strong>{' '}
                                    {t('liveClasses.oneOnOne.point3')}{' '}
                                    <strong>
                                        {t('liveClasses.oneOnOne.point3Bold2')}
                                    </strong>{' '}
                                    {t('liveClasses.oneOnOne.point3End')}
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2">👨‍💻</span>
                                <span>
                                    {t('liveClasses.oneOnOne.point4')}{' '}
                                    <strong>
                                        {t('liveClasses.oneOnOne.point4Bold')}
                                    </strong>{' '}
                                    {t('liveClasses.oneOnOne.point4End')}
                                </span>
                            </li>
                            <li className="flex items-start">
                                <span className="mr-2">📅</span>
                                <span>
                                    <strong>
                                        {t('liveClasses.oneOnOne.point5Bold')}
                                    </strong>{' '}
                                    {t('liveClasses.oneOnOne.point5')}
                                </span>
                            </li>
                        </ul>
                        <PrimaryButton
                            as="link"
                            href="https://calendly.com/d/cszy-5rb-kzt/1-a-1-con-alberto-de-conquer-blocks"
                            target="_blank"
                            size="full-width"
                            className="uppercase tracking-wide"
                        >
                            {t('liveClasses.oneOnOne.button')}
                        </PrimaryButton>
                    </div>
                </div>

                {/* Dynamic AMA Cards Grid */}
                {amas.length > 0 && (
                    <div className="grid gap-6 md:grid-cols-2">
                        {amas.map((ama) => {
                            const hasHtml = isHtmlContent(ama.description);
                            const descriptionPoints = hasHtml
                                ? []
                                : parseDescription(ama.description);

                            return (
                                <div
                                    key={ama.id}
                                    className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:shadow-md"
                                >
                                    <div className="flex-1 space-y-4 p-6">
                                        {/* Title */}
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {ama.title}
                                        </h3>

                                        {/* Description - HTML or plain text */}
                                        {ama.description && (
                                            <div className="text-gray-700">
                                                {hasHtml ? (
                                                    <div
                                                        className="prose prose-sm max-w-none"
                                                        dangerouslySetInnerHTML={{
                                                            __html: ama.description,
                                                        }}
                                                    />
                                                ) : (
                                                    descriptionPoints.length >
                                                        0 && (
                                                        <ul className="space-y-2">
                                                            {descriptionPoints.map(
                                                                (
                                                                    point,
                                                                    index
                                                                ) => (
                                                                    <li
                                                                        key={`${ama.id}-point-${index}`}
                                                                        className="flex items-start text-sm"
                                                                    >
                                                                        <span className="mr-2">
                                                                            •
                                                                        </span>
                                                                        <span>
                                                                            {
                                                                                point
                                                                            }
                                                                        </span>
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Button at the bottom */}
                                    <div className="p-6 pt-0">
                                        {ama.google_meet_url ? (
                                            <PrimaryButton
                                                as="link"
                                                href={ama.google_meet_url}
                                                target="_blank"
                                                size="full-width"
                                                className="uppercase tracking-wide"
                                            >
                                                {t('liveClasses.enterClass')}
                                            </PrimaryButton>
                                        ) : (
                                            <div className="w-full transform cursor-not-allowed rounded-lg bg-gray-400 px-6 py-3 text-center font-semibold tracking-wide text-white uppercase shadow-md">
                                                {t('liveClasses.enterClass')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Empty state */}
                {amas.length === 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
                        <div className="py-12 text-center">
                            <Video className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900">
                                {t('liveClasses.noClassesTitle')}
                            </h3>
                            <p className="text-gray-600">
                                {t('liveClasses.noClassesDescription')}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
};

const LiveClasses: React.FC<Props> = ({ amas }) => (
    <ThemeWrapper>
        <LiveClassesContent amas={amas} />
    </ThemeWrapper>
);

export default LiveClasses;
